import React, { useEffect, useState, useCallback } from 'react';
import { Slide, ToastContainer } from 'react-toastify';
import * as XLSX from 'xlsx';
import * as S from './styles';
import { OrderCardBoleto } from '../../components/order-card-boleto';
import { useBoletos } from '../../hooks';
import {
  Topbar,
  Navbar,
  DashboardFooter,
  Breadcrumb,
  Loading,
  Pagination,
} from '../../components';
import { DynamicFilter } from '../../components/dynamic-filter';
import {
  IDynamicFilterData,
  IDynamicFilterSelectOptions,
} from '../../interfaces';
import bannerDesktop from '../../assets/banner_site_boletos.png';
import bannerMobile from '../../assets/banner_mobile_boletos.png';
import { formatCNPJ, formatCPF, onlyNumbers } from '../../utils/functions';
import { usePagination } from '../../hooks/usePagination';
import { masks } from '../../constants/masks';

export const Boletos: React.FC = () => {
  const {
    isLoading,
    response,
    responseResumed,
    qtdeRegistros,
    errors,
    responseExcel,
    returnTitles,
    returnTitlesResumed,
  } = useBoletos();

  const today = new Date();
  const startDate = new Date();
  startDate.setDate(today.getDate() - 45);
  const endDate = new Date();
  endDate.setDate(today.getDate() + 45);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const [activeTab, setActiveTab] = useState<string>('open');
  const [bannerSrc, setBannerSrc] = useState(bannerDesktop);

  const [filters, setFilters] = useState<IDynamicFilterData>({
    type: 'razaoSocial',
    term: '',
    dateStart: startDate.toISOString().split('T')[0],
    dateEnd: endDate.toISOString().split('T')[0],
  });

  const { currentPage, pageSize, handlePageChange, resetPagination } =
    usePagination({
      totalItems: qtdeRegistros || 0,
      onPageChange: (currentPage, pageSize) => {
        returnTitles({
          ...filters,
          filter: activeTab,
          currentPage,
          pageSize,
        });
      },
    });

  useEffect(() => {
    filterTitles('open');
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setBannerSrc(bannerMobile);
      } else {
        setBannerSrc(bannerDesktop);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const exportToExcel = (data: any) => {
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(data);

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Boletos');

    const excelBuffer = XLSX.write(workbook, {
      type: 'array',
      bookType: 'xlsx',
    });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(blob);
    const now = new Date();

    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();

    downloadLink.download = `RelatorioBoletos_${day}-${month}-${year}.xlsx`;
    downloadLink.click();
  };

  const handleExport = () => {
    exportToExcel(responseExcel);
  };

  const handleDocumentChange = (value: string, type: string) => {
    const clean = value.replace(/\D/g, '');

    if (type === 'cpf') {
      return formatCPF(clean);
    }

    if (type === 'cnpj') {
      return formatCNPJ(clean);
    }

    return value;
  };

  const filterSelectOptions: IDynamicFilterSelectOptions[] = [
    { value: 'razaoSocial', description: 'Nome/Razão social' },
    { value: 'cpf', description: 'CPF' },
    { value: 'cnpj', description: 'CNPJ' },
    { value: 'notaFiscal', description: 'Nota Fiscal' },
  ];

  const searchOrders = async (filterData: IDynamicFilterData) => {
    resetPagination();

    const prepared = prepareFilters(filterData);
    const mergedFilters = { ...filters, ...prepared };
    setFilters(mergedFilters);

    const newFilters = {
      ...mergedFilters,
      filter: activeTab,
      currentPage: 1,
      pageSize,
    };

    if (activeTab === 'default') {
      await returnTitlesResumed(newFilters);
    } else {
      await returnTitles(newFilters);
    }
  };

  const prepareFilters = (filterData: IDynamicFilterData) => {
    if (filterData.type === 'cpf' || filterData.type === 'cnpj') {
      return {
        ...filterData,
        type: 'cpfCnpj',
        term: filterData.term.replace(/[./-]/g, '').toUpperCase(),
      };
    }

    return filterData;
  };

  const filterTitles = useCallback(
    async (filter: string) => {
      setActiveTab('open');

      switch (filter) {
        case 'open': {
          setActiveTab('open');

          const newFilters: IDynamicFilterData = {
            ...filters,
            dateStart: today.toISOString().split('T')[0],
            dateEnd: endDate.toISOString().split('T')[0],
          };
          setFilters(newFilters);
          resetPagination();
          await returnTitles({
            ...newFilters,
            filter: activeTab,
            currentPage: 1,
            pageSize,
          });

          break;
        }
        case 'closed': {
          setActiveTab('closed');

          const newFilters: IDynamicFilterData = {
            ...filters,
            dateStart: startDate.toISOString().split('T')[0],
            dateEnd: yesterday.toISOString().split('T')[0],
          };
          setFilters(newFilters);
          resetPagination();
          await returnTitles({
            ...newFilters,
            currentPage: 1,
            pageSize,
          });

          break;
        }
        default: {
          setActiveTab('default');

          const newFilters: IDynamicFilterData = {
            ...filters,
            dateStart: startDate.toISOString().split('T')[0],
            dateEnd: endDate.toISOString().split('T')[0],
          };
          setFilters(newFilters);
          resetPagination();
          await returnTitlesResumed({
            ...newFilters,
            currentPage: 1,
            pageSize: 20,
          });

          break;
        }
      }
    },
    [response, filters],
  );

  const handleClearSearch = () => {
    const newFilters: IDynamicFilterData = {
      ...filters,
      term: '',
    };
    setFilters(newFilters);
    searchOrders(newFilters);
  };

  function dataTreatment(filtersToTreat: IDynamicFilterData) {
    if (filtersToTreat.type === 'cpfCnpj') {
      return filtersToTreat;
    }

    return filtersToTreat;
  }

  return (
    <S.Container>
      <Topbar />
      <Navbar />
      <img src={bannerSrc} width="100%" height="auto" alt="Banner" />
      <S.Content>
        <Breadcrumb paths={paths} />
        <S.Title>Portal de Boletos</S.Title>
        <DynamicFilter
          calendar
          filters={filters}
          isLoading={isLoading}
          setFilters={setFilters}
          handleExport={handleExport}
          searchFilters={searchOrders}
          dataTreatment={dataTreatment}
          filterSelectOptions={filterSelectOptions}
          handleClearSearch={handleClearSearch}
        />
        <S.Tabs.TabsNavigation>
          <S.Tabs.TabsSelector>
            <S.Tabs.TabSelector
              className={
                activeTab === 'open'
                  ? 'active'
                  : '' + (isLoading && ' loading-tab')
              }
              onClick={() => filterTitles('open')}
            >
              Títulos abertos
            </S.Tabs.TabSelector>
            <S.Tabs.TabSelector
              className={
                activeTab === 'closed'
                  ? 'active'
                  : '' + (isLoading && ' loading-tab')
              }
              onClick={() => filterTitles('closed')}
            >
              Títulos vencidos
            </S.Tabs.TabSelector>
            <S.Tabs.TabSelector
              className={
                activeTab === 'default'
                  ? 'active'
                  : '' + (isLoading && ' loading-tab')
              }
              onClick={() => filterTitles('')}
            >
              Resumo dos títulos
            </S.Tabs.TabSelector>
          </S.Tabs.TabsSelector>

          {activeTab !== 'default' ? (
            <S.Tabs.TabsContent>
              <thead>
                {/* TABLE HEAD */}
                <S.Tabs.TabsRow>
                  <S.Tabs.TabsHead>Razão social</S.Tabs.TabsHead>
                  <S.Tabs.TabsHead>Vencimento</S.Tabs.TabsHead>
                  <S.Tabs.TabsHead>Nota Fiscal</S.Tabs.TabsHead>
                  <S.Tabs.TabsHead>Parcela</S.Tabs.TabsHead>
                  <S.Tabs.TabsHead>Valor</S.Tabs.TabsHead>
                  <S.Tabs.TabsHead>CNPJ/CPF</S.Tabs.TabsHead>
                  <S.Tabs.TabsHead> </S.Tabs.TabsHead>
                </S.Tabs.TabsRow>
              </thead>
              <tbody>
                {/* TABLE DATA */}
                {!isLoading && (
                  <>
                    {response &&
                      response?.map((elm: any, idx: number) => {
                        return <OrderCardBoleto boleto={elm} key={idx} />;
                      })}
                  </>
                )}
              </tbody>
            </S.Tabs.TabsContent>
          ) : (
            <S.Tabs.TabsContent>
              <thead>
                {/* TABLE HEAD */}
                <S.Tabs.TabsRow>
                  <S.Tabs.TabsHead>Razão social</S.Tabs.TabsHead>
                  <S.Tabs.TabsHead>Total de títulos a vencer</S.Tabs.TabsHead>
                  <S.Tabs.TabsHead>Total de títulos vencidos</S.Tabs.TabsHead>
                  <S.Tabs.TabsHead>Total de títulos</S.Tabs.TabsHead>
                  <S.Tabs.TabsHead>CNPJ/CPF</S.Tabs.TabsHead>
                </S.Tabs.TabsRow>
              </thead>
              <tbody>
                {/* TABLE DATA */}
                {!isLoading && (
                  <>
                    {responseResumed &&
                      responseResumed?.map((elm: any, idx: number) => {
                        return (
                          <OrderCardBoleto
                            boleto={elm}
                            key={idx}
                            isDefault={true}
                          />
                        );
                      })}
                  </>
                )}
              </tbody>
            </S.Tabs.TabsContent>
          )}
        </S.Tabs.TabsNavigation>

        {!isLoading &&
          !errors &&
          qtdeRegistros !== 0 &&
          activeTab !== 'default' && (
            <div
              style={{
                width: '100%',
                display: 'flex',
                padding: '24px 0',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Pagination
                currentPage={currentPage}
                paginationSize={pageSize}
                onPageChange={handlePageChange}
                totalItems={qtdeRegistros}
              />
            </div>
          )}

        {!isLoading && !errors && qtdeRegistros === 0 && (
          <div
            style={{
              width: '100%',
              display: 'flex',
              padding: '24px 0 62px',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <S.Items>
              {!isLoading && !errors && <p>Sem boletos listados.</p>}
            </S.Items>
          </div>
        )}

        {!isLoading && response.length === 0 && (
          <div
            style={{
              width: '100%',
              display: 'flex',
              padding: '24px 0 62px',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <S.Items>{!isLoading && errors && <p>{errors}</p>}</S.Items>
          </div>
        )}

        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {isLoading && <Loading />}
        </div>
      </S.Content>
      <DashboardFooter />
      <ToastContainer
        closeButton={false}
        transition={Slide}
        position="bottom-center"
        autoClose={2000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </S.Container>
  );
};

const paths = [
  {
    title: 'Meu Painel',
    link: '/painel',
  },
  {
    title: 'Portal de Boletos',
    link: '',
  },
];
